import React, { useState } from "react";
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Link,
    Text,
    Textarea,
    VStack,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

/**
 * Contact — cut down to Sam Manger's website feedback (27 Jul 2026): "Leave email address and a
 * contact form". Removed: phone, business hours, emergency support, our office, healthcare
 * providers, response times and the map placeholder.
 *
 * The old form only logged to the console and then said "sent" — nothing was ever delivered. Until
 * the site has a real contact form (a LeadFlow "contact" form, like the waitlist), this one says
 * exactly what it does: it opens the person's own email app with their message filled in.
 */
const CONTACT_EMAIL = "support@surgready.com.au";

const Contact = () => {
    const [name, setName] = useState("");
    const [message, setMessage] = useState("");

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const subject = `SurgReady enquiry${name ? ` from ${name}` : ""}`;
        const body = `${message}\n\n${name}`;
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <NavbarWithCallToAction />

            <Container maxW="720px" py={{ base: 12, md: 20 }}>
                <VStack spacing={10} align="stretch">
                    <VStack spacing={4} textAlign="center">
                        <Heading as="h1" fontSize={{ base: "4xl", md: "6xl" }} fontWeight="500" letterSpacing="-0.02em">
                            Contact us
                        </Heading>
                        <Text fontSize={{ base: "lg", md: "xl" }} color="whiteAlpha.800" lineHeight="1.7">
                            Email us at{" "}
                            <Link href={`mailto:${CONTACT_EMAIL}`} color="#7AB0FF" textDecoration="underline">
                                {CONTACT_EMAIL}
                            </Link>
                            , or send a message below.
                        </Text>
                    </VStack>

                    <Box
                        as="form"
                        onSubmit={onSubmit}
                        bg="rgba(255,255,255,0.03)"
                        border="1px solid"
                        borderColor="whiteAlpha.100"
                        borderRadius="2xl"
                        p={{ base: 6, md: 8 }}
                    >
                        <VStack spacing={5}>
                            <FormControl>
                                <FormLabel fontSize="md">Your name</FormLabel>
                                <Input value={name} onChange={(e) => setName(e.target.value)} minH="48px" fontSize="md" borderColor="whiteAlpha.300" />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="md">Message</FormLabel>
                                <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} fontSize="md" borderColor="whiteAlpha.300" />
                            </FormControl>
                            <Button
                                type="submit"
                                size="lg"
                                minH="56px"
                                w="full"
                                borderRadius="full"
                                bg="white"
                                color="black"
                                _hover={{ bg: "whiteAlpha.900" }}
                                title="Opens your email app with this message ready to send to SurgReady"
                            >
                                Write the email
                            </Button>
                            <Text fontSize="sm" color="whiteAlpha.700" textAlign="center">
                                This opens your own email app with your message filled in, ready for you to send.
                            </Text>
                        </VStack>
                    </Box>
                </VStack>
            </Container>

            <FooterWithFourColumns />
        </Box>
    );
};

export default Contact;
